import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    Box, Paper, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, IconButton, CircularProgress, Autocomplete
} from '@mui/material';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import MUIDataTable from 'mui-datatables';
import { useSnackbar } from 'notistack';
import { getTeachers, saveTeacher, deleteTeacher } from '../../../store/actions/teacherActions';
import { getUsers } from '../../../store/actions/userActions';
import { RootState } from '../../../store/reducers';

const Teachers = () => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const teachers = useSelector((state: RootState) => state.teachers.teachers);
    const isFetching = useSelector((state: RootState) => state.teachers.isFetching);
    const users = useSelector((state: RootState) => state.users.users);

    const [dialogOpen, setDialogOpen] = useState(false);
    const [editItem, setEditItem] = useState<any>(null);
    const [formData, setFormData] = useState<any>({ FirstName: '', email: '', Phone: '', User_id: null });
    const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

    useEffect(() => {
        dispatch(getTeachers());
        dispatch(getUsers());
    }, [dispatch]);

    const handleOpen = (item?: any) => {
        if (item) {
            setEditItem(item);
            setFormData({ FirstName: item.FirstName || '', email: item.email || '', Phone: item.Phone || '', User_id: item.User_id || null });
        } else {
            setEditItem(null);
            setFormData({ FirstName: '', email: '', Phone: '', User_id: null });
        }
        setDialogOpen(true);
    };

    const handleClose = () => {
        setDialogOpen(false);
        setEditItem(null);
        setFormData({ FirstName: '', email: '', Phone: '', User_id: null });
    };

    const handleSave = () => {
        const payload = editItem ? { ...formData, id: editItem.id } : { ...formData };
        dispatch(saveTeacher(payload));
        handleClose();
        enqueueSnackbar('Salvo com sucesso!', { variant: 'success' });
    };

    const handleDelete = (id: number) => {
        dispatch(deleteTeacher(id));
        setDeleteConfirm(null);
        enqueueSnackbar('Excluido com sucesso!', { variant: 'success' });
    };

    const handleChange = (field: string, value: string) => {
        setFormData((prev: any) => ({ ...prev, [field]: value }));
    };

    const columns = [
        {
            name: 'id',
            label: 'ID',
            options: {
                display: false,
                filter: false,
                sort: true,
            }
        },
        {
            name: 'FirstName',
            label: 'Nome',
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: 'email',
            label: 'Email',
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: 'Phone',
            label: 'Telefone',
            options: {
                filter: true,
                sort: true,
            }
        },
        {
            name: 'fermiUser',
            label: 'Usuário',
            options: {
                filter: false,
                sort: true,
                customBodyRenderLite: (dataIndex: number) => {
                    const item = teachers[dataIndex];
                    if (item?.fermiUser) {
                        return item.fermiUser.username || item.fermiUser.email || '';
                    }
                    return item?.User_id ? `ID: ${item.User_id}` : '';
                },
            }
        },
        {
            name: 'actions',
            label: 'Ações',
            options: {
                filter: false,
                sort: false,
                customBodyRenderLite: (dataIndex: number) => {
                    const item = teachers[dataIndex];
                    return (
                        <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => {
                                e.stopPropagation();
                                setDeleteConfirm(item.id);
                            }}
                        >
                            <DeleteIcon />
                        </IconButton>
                    );
                }
            }
        }
    ];

    const options = {
        filterType: 'checkbox' as const,
        selectableRows: 'none' as const,
        responsive: 'standard' as const,
        rowsPerPage: 15,
        rowsPerPageOptions: [15, 30, 50],
        textLabels: {
            body: { noMatch: 'Nenhum registro encontrado', toolTip: 'Ordenar' },
            pagination: { next: 'Próxima', previous: 'Anterior', rowsPerPage: 'Linhas por página:', displayRows: 'de' },
            toolbar: { search: 'Buscar', downloadCsv: 'Download CSV', print: 'Imprimir', viewColumns: 'Colunas', filterTable: 'Filtrar' },
            filter: { all: 'Todos', title: 'Filtros', reset: 'Limpar' },
        },
        onRowClick: (_rowData: any, rowMeta: any) => {
            const item = teachers[rowMeta.dataIndex];
            handleOpen(item);
        },
    };

    if (isFetching) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h5">Professores</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
                    Novo Professor
                </Button>
            </Box>

            <Paper>
                <MUIDataTable
                    title=""
                    data={teachers || []}
                    columns={columns}
                    options={options}
                />
            </Paper>

            {/* Create/Edit Dialog */}
            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle>{editItem ? 'Editar Professor' : 'Novo Professor'}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        <TextField
                            label="Nome"
                            value={formData.FirstName}
                            onChange={(e) => handleChange('FirstName', e.target.value)}
                            fullWidth
                            required
                        />
                        <TextField
                            label="Email"
                            type="email"
                            value={formData.email}
                            onChange={(e) => handleChange('email', e.target.value)}
                            fullWidth
                        />
                        <TextField
                            label="Telefone"
                            value={formData.Phone}
                            onChange={(e) => handleChange('Phone', e.target.value)}
                            fullWidth
                        />
                        <Autocomplete
                            options={users || []}
                            getOptionLabel={(option: any) => option.username || ''}
                            value={users?.find((u: any) => u.id === formData.User_id) || null}
                            onChange={(_e, newValue: any) => {
                                setFormData((prev: any) => ({ ...prev, User_id: newValue ? newValue.id : null }));
                            }}
                            isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
                            renderInput={(params) => (
                                <TextField {...params} label="Usuário (FermiUser)" placeholder="Selecione um usuário..." />
                            )}
                            fullWidth
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Cancelar</Button>
                    <Button onClick={handleSave} variant="contained" disabled={!formData.FirstName}>
                        Salvar
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Confirmation Dialog */}
            <Dialog open={deleteConfirm !== null} onClose={() => setDeleteConfirm(null)}>
                <DialogTitle>Confirmar Exclusão</DialogTitle>
                <DialogContent>
                    <Typography>Tem certeza que deseja excluir este professor?</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                    <Button
                        onClick={() => deleteConfirm !== null && handleDelete(deleteConfirm)}
                        color="error"
                        variant="contained"
                    >
                        Excluir
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Teachers;
